import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { compactNaira, naira } from "../../lib/format";
import { FileDown, FileText } from "lucide-react";
import { C } from "./AccountingLayout";
import { downloadReport } from "./exportHelpers";

export default function TaxVAT() {
  const { locationId, period } = useOutletContext();
  const [vat, setVat] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    api.get("/reports/vat-summary", { params: locationId ? { location_id: locationId } : {} }).then(setVat).catch(() => {});
    api.get("/taxes").then(setTaxes).catch(() => setTaxes([{ name: "VAT", rate: 7.5, is_default: true }]));
  }, [locationId]);
  const doExport = async (fmt) => {
    setBusy(fmt);
    const date = new Date().toISOString().slice(0, 10);
    const p = locationId ? { location_id: locationId } : {};
    await downloadReport(`/reports/exports/vat-summary.${fmt}`, `vat-summary-${date}.${fmt}`, p);
    setBusy(null);
  };
  if (!vat) return <div className="rounded-xl bg-white border p-8 text-center" style={{ borderColor: C.border, color: C.softMuted }}>Loading…</div>;
  return (
    <div className="space-y-4" data-testid="tax-vat-page">
      <div className="flex justify-end gap-2">
        <button onClick={() => doExport("csv")} disabled={busy === "csv"} className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50" style={{ background: C.gold, color: "#FFFFFF" }} data-testid="vat-export-csv"><FileDown size={13} /> {busy === "csv" ? "…" : "CSV"}</button>
        <button onClick={() => doExport("pdf")} disabled={busy === "pdf"} className="h-9 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50 text-white" style={{ background: C.burgundy }} data-testid="vat-export-pdf"><FileText size={13} /> {busy === "pdf" ? "…" : "PDF"}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }}>
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>VAT Summary <span className="text-[13px] font-normal" style={{ color: C.softMuted }}>(All Time)</span></h3>
          <div className="space-y-2.5 text-[13.5px]">
            <div className="flex justify-between"><span style={{ color: C.muted }}>Taxable Sales</span><span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>{naira(vat.taxable_sales)}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>VAT Collected (Output)</span><span className="font-brand font-bold tabular-nums" style={{ color: C.green }}>{naira(vat.vat_collected)}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>VAT Paid (Input)</span><span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>{naira(vat.vat_paid)}</span></div>
            <div className="flex justify-between border-t-2 pt-2" style={{ borderColor: C.borderSoft }}>
              <span className="font-brand font-bold text-[15px]" style={{ color: C.text }}>Net VAT Payable</span>
              <span className="font-brand font-bold text-[18px] tabular-nums" style={{ color: C.red }}>{naira(vat.vat_payable)}</span>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }}>
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Tax Rules</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}><th className="py-2">Name</th><th className="py-2 text-right">Rate</th><th className="py-2 text-right">Default</th></tr></thead>
            <tbody>{(taxes || []).map((t, i) => (
              <tr key={i} className="border-b" style={{ borderColor: C.borderSoft }}>
                <td className="py-2 font-semibold" style={{ color: C.text }}>{t.name}</td>
                <td className="py-2 text-right tabular-nums">{t.rate}%</td>
                <td className="py-2 text-right">{t.is_default ? <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: C.greenTint, color: C.green }}>Default</span> : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
          <div className="text-[11px] mt-3" style={{ color: C.softMuted }}>Tax rules are applied at the POS/order engine. Contact IT to update rates.</div>
        </div>
      </div>
    </div>
  );
}
